import { googleApi } from './googleApi'
import type { AppRepository, AttendanceRegistration } from './repositoryTypes'
import type { SessionGenerationInput } from '../domain/sessionGeneration'
import { hasUsablePairingToken } from '../domain/pairingQr'
import type { ScanClaim } from '../domain/registrationClaim'
import { attendanceQrForSession, nextAttendanceQr } from '../domain/attendanceQr'
import { DEFAULT_TIMING, validateTimingSettings, type TimingSettings } from '../domain/timing'
import type { AcademicSession, AppState, AttendanceRecord, AuditLog, Course, CoursePeriod, DisplayLink, Enrollment, Student } from '../domain/types'

export type ApiCaller = <T>(action: string, payload?: Record<string, unknown>, idToken?: string) => Promise<T>

const EMPTY_STATE: AppState = { version: 0, settings: { ...DEFAULT_TIMING }, courses: [], periods: [], students: [], enrollments: [], sessions: [], attendance: [], audit: [], displays: [], attendanceTokens: [] }
const time = (value: unknown) => value ? new Date(String(value)).getTime() : undefined
const bool = (value: unknown) => value === true || String(value).toLowerCase() === 'true'
const value = (input: unknown): '' | 0 | 1 | string => input === 1 || input === '1' ? 1 : input === 0 || input === '0' ? 0 : String(input || '')

export interface BootstrapPayload {
  settings?: TimingSettings
  courses?: Record<string, unknown>[]
  periods?: Record<string, unknown>[]
  students?: Record<string, unknown>[]
  enrollments?: Record<string, unknown>[]
  sessions?: Record<string, unknown>[]
  attendance?: Record<string, unknown>[]
  audit?: Record<string, unknown>[]
  displays?: Record<string, unknown>[]
}

export function mapBootstrap(raw: BootstrapPayload, version: number): AppState {
  const courses: Course[] = (raw.courses || []).map((row) => ({ id: String(row.id), name: String(row.name) }))
  const students: Student[] = (raw.students || []).map((row) => ({ id: String(row.id), cui: String(row.cui), name: String(row.name), active: !('active' in row) || bool(row.active) }))
  const enrollments: Enrollment[] = (raw.enrollments || []).map((row) => {
    let deviceIds: string[] = []
    try { deviceIds = JSON.parse(String(row.device_ids_json || '[]')) as string[] } catch { /* invalid legacy cell */ }
    return { id: String(row.id), periodId: String(row.course_period_id), studentId: String(row.student_id), deviceIds, active: !('active' in row) || bool(row.active), name: row.display_name ? String(row.display_name) : undefined, participation: row.participation as Enrollment['participation'] }
  })
  const sessions: AcademicSession[] = (raw.sessions || []).map((row) => ({
    id: String(row.id), periodId: String(row.course_period_id), week: Number(row.week), sessionNumber: Number(row.session_number),
    type: String(row.type || 'Otro') as AcademicSession['type'], date: String(row.date || ''), startTime: String(row.start_time || ''), status: String(row.status) as AcademicSession['status'],
    startedAt: time(row.started_at), closesAt: time(row.closes_at), finalizedAt: time(row.finalized_at),
    googleSyncStatus: row.google_sync_status as AcademicSession['googleSyncStatus'], googleSyncError: row.google_sync_error ? String(row.google_sync_error) : undefined,
    timing: Number(row.attendance_window_seconds) > 0 ? {
      attendance_window_seconds: Number(row.attendance_window_seconds),
      qr_rotation_seconds: Number(row.qr_rotation_seconds) || DEFAULT_TIMING.qr_rotation_seconds,
      attendance_token_ttl_seconds: Number(row.attendance_token_ttl_seconds) || DEFAULT_TIMING.attendance_token_ttl_seconds,
    } : undefined,
  }))
  const periods: CoursePeriod[] = (raw.periods || []).map((row) => ({
    id: String(row.id), courseId: String(row.course_id), label: String(row.label), weeks: Number(row.weeks), sessionsPerWeek: Number(row.sessions_per_week), status: String(row.status) as CoursePeriod['status'],
    enrollmentIds: enrollments.filter((item) => item.periodId === String(row.id)).map((item) => item.id),
    sessionIds: sessions.filter((item) => item.periodId === String(row.id)).map((item) => item.id),
    sheet: row.public_spreadsheet_id ? { spreadsheetId: String(row.public_spreadsheet_id), tabName: String(row.public_tab_name || '') } : undefined,
  }))
  const attendance: AttendanceRecord[] = (raw.attendance || []).map((row) => ({
    id: String(row.id), sessionId: String(row.session_id), enrollmentId: String(row.enrollment_id), value: value(row.value), status: String(row.status) as AttendanceRecord['status'],
    method: String(row.method) as AttendanceRecord['method'], timestamp: time(row.timestamp) || 0, deviceId: row.device_id ? String(row.device_id) : undefined, userAgent: row.user_agent ? String(row.user_agent) : undefined,
  }))
  const audit: AuditLog[] = (raw.audit || []).map((row) => ({
    id: String(row.id), timestamp: time(row.timestamp) || 0, action: String(row.action), actor: String(row.actor) as AuditLog['actor'], courseId: row.course_id ? String(row.course_id) : undefined,
    periodId: row.course_period_id ? String(row.course_period_id) : undefined, sessionId: row.session_id ? String(row.session_id) : undefined, enrollmentId: row.enrollment_id ? String(row.enrollment_id) : undefined,
    previousValue: row.previous_value, newValue: row.new_value, detail: row.detail ? String(row.detail) : undefined,
  }))
  const displays: DisplayLink[] = (raw.displays || []).map((row) => ({ displayId: String(row.display_id), state: String(row.state) as DisplayLink['state'], linkToken: '', tokenExpiresAt: time(row.token_expires_at) || 0, consumed: bool(row.consumed), activeSessionId: row.active_session_id ? String(row.active_session_id) : undefined }))
  return { version, settings: validateTimingSettings(raw.settings ?? { ...DEFAULT_TIMING }), courses, periods, students, enrollments, sessions, attendance, audit, displays, attendanceTokens: [] }
}

interface DisplayPayload {
  serverTime?: number
  display: DisplayLink
  course?: Course
  period?: CoursePeriod
  session?: AcademicSession
  token?: { token: string; sessionId: string; expiresAt: number; issuedAt: number }
  present?: number
  total?: number
}
interface SessionSummary { sessionId: string; status: AcademicSession['status']; closesAt: number | null }
interface LivePayload extends SessionSummary { serverTime: number; presentCount: number; records: AttendanceRecord[]; displays: DisplayLink[] }
interface TeacherStatus { serverTime: number; sessions: SessionSummary[]; displays: DisplayLink[] }
const attendanceCredential = (token: string, claim?: ScanClaim) => claim
  ? { claim: claim.ticket, sessionId: claim.sessionId, deviceId: claim.deviceId }
  : { token }

export class GoogleRepository implements AppRepository {
  readonly mode: 'google' | 'supabase'
  private state: AppState = { ...EMPTY_STATE }
  private error = ''
  private teacherToken: string | null = null
  private teacherRefresh: Promise<void> | null = null
  private liveRefresh: Promise<void> | null = null
  private structuralRevision = 0
  private readonly claimRequests = new Map<string, Promise<ScanClaim>>()
  private readonly linkRequests = new Map<string, Promise<string>>()
  private readonly displayRefreshes = new Map<string, Promise<void>>()
  private readonly displayEpochs = new Map<string, number>()
  private readonly displayTimes = new Map<string, number>()
  private readonly displayEnsures = new Map<string, Promise<DisplayLink>>()
  private readonly listeners = new Set<() => void>()
  constructor(private readonly api: ApiCaller = googleApi, mode: 'google' | 'supabase' = 'google') { this.mode = mode }
  getSnapshot = () => this.state
  getError = () => this.error
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => this.listeners.delete(listener) }
  setTeacherToken = (token: string | null) => { this.teacherToken = token }
  protected setOperationalToken(token: string | null) { if (this.teacherToken !== token) this.structuralRevision++; this.teacherToken = token }
  protected getOperationalRevision() { return this.structuralRevision }
  protected clearOperationalState() { this.replace({ ...EMPTY_STATE }) }
  private emit() { this.listeners.forEach((listener) => listener()) }
  private fail(reason: unknown): never { this.error = reason instanceof Error ? reason.message : 'No se pudo conectar con Apps Script.'; this.emit(); throw reason }
  private replace(next: AppState) { this.error = ''; this.state = { ...next, version: this.state.version + 1 }; this.emit() }
  protected applyOperationalLive(raw: BootstrapPayload) {
    const mapped = mapBootstrap(raw, this.state.version + 1)
    this.replace({ ...this.state, sessions: mapped.sessions, attendance: mapped.attendance, displays: mapped.displays })
  }
  protected reportOperationalError(reason: unknown): never { return this.fail(reason) }
  protected async teacher<T>(action: string, payload: Record<string, unknown> = {}) {
    if (!this.teacherToken) throw new Error('AUTH_REQUIRED')
    return this.api<T>(action, payload, this.teacherToken)
  }
  refreshTeacher() {
    if (this.teacherRefresh) return this.teacherRefresh
    const revision = ++this.structuralRevision
    this.teacherRefresh = (async () => { try { const raw = await this.teacher<BootstrapPayload>('bootstrap'); if (revision === this.structuralRevision) this.replace(mapBootstrap(raw, this.state.version + 1)) } catch (reason) { this.fail(reason) } })()
      .finally(() => { this.teacherRefresh = null })
    return this.teacherRefresh
  }
  protected async refreshAfterMutation() {
    this.structuralRevision++
    // A bootstrap already in flight may predate the write. Load again after it settles.
    if (this.teacherRefresh) await this.teacherRefresh.catch(() => undefined)
    await this.refreshTeacher()
  }
  pollTeacher(sessionId?: string): Promise<void> {
    if (this.teacherRefresh) return this.teacherRefresh
    if (this.liveRefresh) return this.liveRefresh
    const revision = this.structuralRevision
    this.liveRefresh = this.pollLive(sessionId, revision).catch((reason) => this.fail(reason)).finally(() => { this.liveRefresh = null })
    return this.liveRefresh
  }
  private async pollLive(sessionId: string | undefined, revision: number) {
    let active = this.state.sessions.find((session) => session.status === 'ACTIVE')
    if (!active) {
      const raw = await this.teacher<TeacherStatus>('teacher.status')
      if (revision !== this.structuralRevision) return
      const unknown = raw.sessions.some((session) => !this.state.sessions.some((known) => known.id === session.sessionId))
      const disappeared = this.state.sessions.some((session) => ['ACTIVE', 'REVIEW'].includes(session.status) && !raw.sessions.some((remote) => remote.sessionId === session.id))
      if (unknown || disappeared) { await this.refreshTeacher(); return }
      this.replace({ ...this.state, sessions: this.state.sessions.map((session) => {
        const summary = raw.sessions.find((item) => item.sessionId === session.id)
        return summary ? { ...session, status: summary.status, closesAt: summary.closesAt ?? undefined } : session
      }), displays: raw.displays })
      active = this.state.sessions.find((session) => session.status === 'ACTIVE')
    }
    const target = active || this.state.sessions.find((session) => session.id === sessionId && session.status === 'REVIEW')
    if (!target) return
    const raw = await this.teacher<LivePayload>('session.live', { sessionId: target.id })
    if (revision !== this.structuralRevision) return // Ignore a poll predating a structural refresh.
    this.replace({ ...this.state,
      sessions: this.state.sessions.map((session) => session.id === raw.sessionId ? { ...session, status: raw.status, closesAt: raw.closesAt ?? undefined } : session),
      attendance: [...this.state.attendance.filter((record) => record.sessionId !== raw.sessionId), ...raw.records],
      displays: [...this.state.displays.filter((display) => display.activeSessionId !== raw.sessionId && !raw.displays.some((remote) => remote.displayId === display.displayId)), ...raw.displays],
    })
  }
  async getSettings() {
    try {
      const settings = validateTimingSettings(await this.teacher<TimingSettings>('settings.get'))
      this.replace({ ...this.state, settings }); return settings
    } catch (reason) { return this.fail(reason) }
  }
  async updateSettings(input: TimingSettings) {
    try {
      const settings = validateTimingSettings(await this.teacher<TimingSettings>('settings.update', { settings: validateTimingSettings(input) }))
      await this.refreshAfterMutation()
      return settings
    } catch (reason) { return this.fail(reason) }
  }
  ensureDisplay(displayId: string): Promise<DisplayLink> {
    const pending = this.displayEnsures.get(displayId)
    if (pending) return pending
    const promise = this.ensureDisplayToken(displayId).finally(() => this.displayEnsures.delete(displayId))
    this.displayEnsures.set(displayId, promise)
    return promise
  }
  private async ensureDisplayToken(displayId: string) {
    const existing = this.state.displays.find((item) => item.displayId === displayId)
    if (existing && (existing.state !== 'UNLINKED' || hasUsablePairingToken(existing))) return existing
    try {
      await this.refreshDisplay(displayId)
      const recovered = this.state.displays.find((item) => item.displayId === displayId)
      if (recovered && (recovered.state !== 'UNLINKED' || hasUsablePairingToken(recovered))) return recovered
    } catch (reason) {
      if (!(reason instanceof Error) || !reason.message.includes('DISPLAY_NOT_FOUND')) throw reason
    }
    try {
      const raw = await this.api<{ displayId: string; linkToken: string; expiresAt: number }>('display.create', { displayId })
      const display: DisplayLink = { displayId: raw.displayId, state: 'UNLINKED', linkToken: raw.linkToken, tokenExpiresAt: raw.expiresAt, consumed: false }
      this.replace({ ...this.state, displays: [...this.state.displays.filter((item) => item.displayId !== displayId), display] }); return display
    } catch (reason) { return this.fail(reason) }
  }
  refreshDisplay(displayId: string): Promise<void> {
    const pending = this.displayRefreshes.get(displayId)
    if (pending) return pending
    const epoch = this.displayEpochs.get(displayId) ?? 0
    const promise = this.readDisplay(displayId, epoch).finally(() => { if (this.displayRefreshes.get(displayId) === promise) this.displayRefreshes.delete(displayId) })
    this.displayRefreshes.set(displayId, promise)
    return promise
  }
  private async readDisplay(displayId: string, epoch: number) {
    try {
      const raw = await this.api<DisplayPayload>('display.status', { displayId })
      if ((this.displayEpochs.get(displayId) ?? 0) !== epoch) return
      if (raw.serverTime !== undefined && raw.serverTime < (this.displayTimes.get(displayId) ?? 0)) return
      const known = this.state.sessions.find((session) => session.id === raw.session?.id)
      if (known && ['REVIEW', 'FINALIZED', 'CANCELLED'].includes(known.status) && raw.session?.status === 'ACTIVE') return
      if (raw.serverTime !== undefined) this.displayTimes.set(displayId, raw.serverTime)
      const previous = this.state.displays.find((item) => item.displayId === displayId)
      // Status intentionally omits the secret: Sheets stores only its hash.
      // Retain our in-memory token only for the same unconsumed issuance.
      const retainToken = previous && hasUsablePairingToken(previous) && raw.display.state === 'UNLINKED' &&
        !raw.display.consumed && raw.display.tokenExpiresAt === previous.tokenExpiresAt
      const display = { ...raw.display, linkToken: retainToken ? previous.linkToken : '' }
      const displays = [...this.state.displays.filter((item) => item.displayId !== displayId), display]
      const sessions = raw.session ? [...this.state.sessions.filter((item) => item.id !== raw.session!.id), raw.session] : this.state.sessions
      const periods = raw.period ? [...this.state.periods.filter((item) => item.id !== raw.period!.id), { ...raw.period, publicEnrollmentCount: raw.total }] : this.state.periods
      const courses = raw.course ? [...this.state.courses.filter((item) => item.id !== raw.course!.id), raw.course] : this.state.courses
      const attendance = raw.session ? Array.from({ length: raw.present || 0 }, (_, index) => ({ id: `public-${raw.session!.id}-${index}`, sessionId: raw.session!.id, enrollmentId: `public-${index}`, value: 1 as const, status: 'PRESENT' as const, method: 'QR' as const, timestamp: 0 })) : []
      const session = sessions.find((item) => item.id === display.activeSessionId)
      const token = display.state === 'SHOWING_ATTENDANCE' ? nextAttendanceQr(attendanceQrForSession(this.state.attendanceTokens, session), raw.token, session) : undefined
      this.replace({ ...this.state, displays, sessions, periods, courses, attendance, attendanceTokens: token ? [token] : [] })
    } catch (reason) { if ((this.displayEpochs.get(displayId) ?? 0) === epoch) this.fail(reason) }
  }
  async resetDisplay(displayId: string) {
    this.displayEpochs.set(displayId, (this.displayEpochs.get(displayId) ?? 0) + 1)
    this.displayRefreshes.delete(displayId)
    this.replace({ ...this.state, displays: this.state.displays.filter((item) => item.displayId !== displayId), attendanceTokens: [] }); await this.ensureDisplay(displayId)
  }
  linkDisplay(token: string): Promise<string> {
    const pending = this.linkRequests.get(token)
    if (pending) return pending
    const promise = this.performLink(token).finally(() => this.linkRequests.delete(token))
    this.linkRequests.set(token, promise)
    return promise
  }
  private async performLink(token: string) { try { const result = await this.teacher<{ displayId: string }>('display.link', { token }); await this.refreshTeacher(); return result.displayId } catch (reason) { return this.fail(reason) } }
  async unlinkDisplay(displayId: string) { try { await this.teacher('display.unlink', { displayId }); await this.refreshTeacher() } catch (reason) { this.fail(reason) } }
  async startSession(sessionId: string, displayId: string) { try { await this.teacher('session.start', { sessionId, displayId }); await this.refreshAfterMutation() } catch (reason) { this.fail(reason) } }
  async enterReview(sessionId: string) { try { await this.teacher('session.review', { sessionId }); await this.refreshAfterMutation() } catch (reason) { this.fail(reason) } }
  async resolveAttendanceContext(token: string) {
    try {
      const raw = await this.api<DisplayPayload>('attendance.context', { token })
      this.replace({ ...EMPTY_STATE, version: this.state.version + 1, courses: raw.course ? [raw.course] : [], periods: raw.period ? [raw.period] : [], sessions: raw.session ? [raw.session] : [], attendanceTokens: raw.token ? [raw.token] : [] })
    } catch (reason) { this.fail(reason) }
  }
  createScanClaim(token: string, deviceId: string): Promise<ScanClaim> {
    const key = JSON.stringify([token, deviceId])
    const pending = this.claimRequests.get(key)
    if (pending) return pending
    const promise = this.api<DisplayPayload & { claim: ScanClaim }>('attendance.claim', { token, deviceId }).then((raw) => {
      this.replace({ ...EMPTY_STATE, version: this.state.version + 1, courses: raw.course ? [raw.course] : [], periods: raw.period ? [raw.period] : [], sessions: raw.session ? [raw.session] : [], attendanceTokens: raw.token ? [raw.token] : [] })
      return raw.claim
    }).catch((reason) => this.fail(reason)).finally(() => this.claimRequests.delete(key))
    this.claimRequests.set(key, promise)
    return promise
  }
  async resolveStudent(token: string, cui: string, claim?: ScanClaim) { try { return (await this.api<{ student: Pick<Student, 'id' | 'cui' | 'name'> }>('attendance.resolve', { ...attendanceCredential(token, claim), cui })).student } catch (reason) { return this.fail(reason) } }
  async registerAttendance(token: string, cui: string, deviceId: string, userAgent: string, claim?: ScanClaim) { try { return await this.api<AttendanceRegistration>('attendance.register', { ...attendanceCredential(token, claim), cui, deviceId, userAgent }) } catch (reason) { return this.fail(reason) } }
  async setAttendance(sessionId: string, enrollmentId: string, attendanceValue: '' | 0 | 1 | string) { try { await this.teacher('attendance.override', { sessionId, enrollmentId, value: attendanceValue }); await this.refreshAfterMutation() } catch (reason) { this.fail(reason) } }
  async finalizeSession(sessionId: string) { try { await this.teacher('session.finalize', { sessionId }); await this.refreshAfterMutation() } catch (reason) { this.fail(reason) } }
  async archivePeriod(periodId: string) { try { await this.teacher('period.archive', { periodId }); await this.refreshAfterMutation() } catch (reason) { this.fail(reason) } }
  async duplicatePeriod(periodId: string, label: string, roster: 'copy' | 'empty') { try { const result = await this.teacher<{ periodId: string }>('period.duplicate', { sourcePeriodId: periodId, label, roster }); await this.refreshAfterMutation(); return result.periodId } catch (reason) { return this.fail(reason) } }
  async generateSessions(input: SessionGenerationInput) {
    try {
      const result = await this.teacher<{ created: number; skipped: number; total: number }>('session.generate', {
        course_period_id: input.coursePeriodId,
        week_one_start_date: input.weekOneStartDate,
        weekly_sessions: input.weeklySessions.map((item) => ({ session_number: item.sessionNumber, weekday: item.weekday, start_time: item.startTime, type: item.type })),
      })
      await this.refreshAfterMutation(); return result
    } catch (reason) { return this.fail(reason) }
  }
  async importRoster(periodId: string, students: { cui: string; name: string }[]): Promise<import('../domain/rosterImport').ImportSummary | void> { try { await this.teacher('roster.import', { periodId, students }); await this.refreshAfterMutation() } catch (reason) { this.fail(reason) } }
  async resetMock() { throw new Error('El modo Google no permite cargar datos demo.') }
}
